import React, { useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import PopoverPanel, { PositionString } from 'clean/components/common/PopoverPanel/PopoverPanel';
import { ReactComponent as ArrowDown } from 'assets/icons/chevron.svg';

import styles from './DropDown.module.scss';

interface AvailableClassNames {
  outer: string;
  outerSelected: string;
  inner: string;
  groupTitle: string;
}

interface Props {
  options?: any[];
  value?: string;
  disabled?: boolean;
  onChange?: (value: any, option: any) => any;
  emptyItemLabel?: string;
  labelProperty?: string;
  idProperty?: string;
  groupProperty?: string;

  renderRow?: ({
    item,
    isSelected,
    classes,
    onClick,
    close,
  }: {
    item: any;
    isSelected: boolean;
    classes: AvailableClassNames;
    onClick: (item: any) => void;
    close: () => void;
  }) => React.ReactNode;
  renderPanel?: ({
    classes,
    close,
  }: {
    classes: AvailableClassNames;
    close: () => void;
  }) => React.ReactNode;
  iconStart?: React.ReactNode;
  targetElementPos?: PositionString;
  childrenElementPos?: PositionString;
  paddingVertical?: number;
  paddingHorizontal?: number;
  showChevron?: boolean;
  inputClassName?: string;
  containerClassName?: string;
  showSelectedOption?: boolean;
}

const ButtonDropDown: React.FC<Props> = ({
  value,
  options = [],
  onChange,
  labelProperty = 'label',
  idProperty = 'id',
  groupProperty,
  emptyItemLabel = '',
  childrenElementPos = 'top.left',
  targetElementPos = 'bottom.left',
  paddingVertical = 2,
  paddingHorizontal = 0,
  iconStart,
  renderRow,
  renderPanel,
  showChevron = true,
  showSelectedOption = true,
  inputClassName = '',
  containerClassName = '',
}) => {
  const { t } = useTranslation();
  const [optionsVisible, setOptionsVisible] = useState(false);
  const containerNode = useRef(null);

  let selectedOption = useMemo(() => {
    return options.find(option => option[idProperty] === value) || null;
  }, [value, options, idProperty]);

  const allOptions = useMemo(() => {
    if (!selectedOption || emptyItemLabel) {
      return [
        { [idProperty]: null, [labelProperty]: emptyItemLabel || t('common:ButtonDropDownSelect') },
        ...options,
      ];
    }

    return options;
  }, [emptyItemLabel, options, selectedOption, idProperty, labelProperty, t]);

  if (!selectedOption) {
    selectedOption = allOptions[0];
  }

  const handleSelection = option => {
    option && option && onChange(option[idProperty], option);
    setOptionsVisible(false);

    if (containerNode.current) {
      containerNode.current.blur();
    }
  };

  const classesAsProps: AvailableClassNames = {
    outer: styles.optionOuter,
    outerSelected: styles.optionSelected,
    inner: styles.option,
    groupTitle: styles.groupTitle,
  };

  return (
    <div ref={containerNode} className={classNames(styles.dropDownContainer, containerClassName)}>
      <div
        className={classNames(styles.input, inputClassName, {
          [styles.inputWithStartIcon]: !!iconStart,
        })}
        onClick={() => setOptionsVisible(true)}
      >
        {iconStart && <div className={styles.iconStart}>{iconStart}</div>}
        <div
          className={classNames(styles.inputText, { [styles.inputTextWithStartIcon]: !!iconStart })}
        >
          {selectedOption[labelProperty]}
        </div>
        <div
          className={classNames(styles.arrow, {
            [styles.arrowHidden]: !showChevron,
            [styles.arrowReversed]: optionsVisible,
          })}
        >
          <ArrowDown />
        </div>
      </div>

      {optionsVisible && (
        <PopoverPanel
          childrenAtleastFullWidth={true}
          targetRef={containerNode}
          targetElementPos={targetElementPos}
          childrenElementPos={childrenElementPos}
          paddingVertical={paddingVertical}
          paddingHorizontal={paddingHorizontal}
          onBackdropClick={() => {
            setOptionsVisible(false);
          }}
        >
          <div className={styles.dropdownPanel} tabIndex={1}>
            {renderPanel
              ? renderPanel({
                  classes: classesAsProps,
                  close: () => handleSelection(undefined),
                })
              : allOptions.map((item, index) => {
                  const isSelected = item[idProperty] === selectedOption[idProperty];

                  const groupTitle: string = (() => {
                    if (!groupProperty) {
                      return null;
                    }

                    if (
                      (index === 0 && item[groupProperty]) ||
                      (index !== 0 && allOptions[index - 1][groupProperty] !== item[groupProperty])
                    ) {
                      return item[groupProperty];
                    }

                    return null;
                  })();

                  if (!showSelectedOption && isSelected) {
                    return null;
                  }

                  return renderRow
                    ? renderRow({
                        item,
                        isSelected,
                        classes: classesAsProps,
                        onClick: item => handleSelection(item),
                        close: () => handleSelection(undefined),
                      })
                    : [
                        groupTitle ? (
                          <div key={groupTitle} className={styles.groupTitle}>
                            {groupTitle}
                          </div>
                        ) : null,
                        <div
                          key={item[idProperty]}
                          className={classNames(styles.optionOuter, {
                            [styles.optionSelected]: isSelected,
                          })}
                          onClick={() => handleSelection(item)}
                        >
                          <div className={styles.option}>{item[labelProperty]}</div>
                        </div>,
                      ];
                })}
          </div>
        </PopoverPanel>
      )}
    </div>
  );
};

export default ButtonDropDown;
